import React, { useState } from "react";
import { Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import {
  type ConnectionEditState,
  type ConnectionEditStateUpdater,
  type Status,
  type SourceStatusMapping,
} from "@onetasklist/shared";
import {
  useSharePointSourceSettingsText,
  useSharePointSourceSettingsViewModel,
} from "@onetasklist/shared/sources/sharepoint";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSiteTagPicker } from "./components/SourceSiteTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";
import { StatusMappingSection } from "./components/StatusMappingSection";

interface SharePointSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
  boardStatuses: Status[];
}

export const SharePointSourceSettings: React.FC<
  SharePointSourceSettingsProps
> = ({ editingConnection, onUpdate, sectionOffset, boardStatuses }) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useSharePointSourceSettingsText(t);
  const vm = useSharePointSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [listQuery, setListQuery] = useState("");
  const [meFilterColumnQuery, setMeFilterColumnQuery] = useState("");
  const [statusFilterColumnQuery, setStatusFilterColumnQuery] = useState("");
  const [statusesToLoadQuery, setStatusesToLoadQuery] = useState("");
  const [doneStatusQuery, setDoneStatusQuery] = useState("");
  const [dueDateColumnQuery, setDueDateColumnQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <>
      <SettingsSectionCard
        sectionOffset={sectionOffset}
        title={t(
          "board.connections.catalogue.sourceSettings.sharepoint.sectionSite",
        )}
      >
        <Field
          orientation="horizontal"
          label={text.siteLabelText}
          hint={`${text.siteHintText} ${text.siteSearchMinCharsText}`}
        >
          <SourceSiteTagPicker
            selectedSiteId={
              vm.selectedSiteId === vm.noneOptionValue
                ? undefined
                : vm.selectedSiteId
            }
            selectedSiteLabel={vm.selectedSiteDisplayName || text.notSetText}
            query={vm.siteQuery}
            placeholder={text.siteSearchPlaceholderText}
            options={vm.sites.map((site) => ({
              id: site.id,
              label: site.displayName || site.name || site.id,
              secondaryLabel: site.webUrl,
            }))}
            onQueryChange={vm.onSiteQueryChange}
            onSelectSite={vm.onSelectSite}
            typeToSearchText={text.siteSearchMinCharsText}
            clearSelectionText={text.notSetText}
            isLoading={vm.isSearchingSites}
          />
        </Field>

        {vm.selectedSiteWebUrl && (
          <Field orientation="horizontal" label={text.siteUrlLabelText}>
            <a href={vm.selectedSiteWebUrl} target="_blank" rel="noreferrer">
              {vm.selectedSiteWebUrl}
            </a>
          </Field>
        )}

        {vm.selectedSiteId !== vm.noneOptionValue && (
          <Field
            orientation="horizontal"
            label={text.listLabelText}
            hint={text.listHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedListId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedListId
              }
              selectedLabel={vm.selectedListDisplayName}
              query={listQuery}
              placeholder={text.listPlaceholderText}
              options={vm.lists.map((list) => ({
                id: list.id,
                label: list.displayName || list.name || list.id,
              }))}
              onQueryChange={setListQuery}
              onSelect={vm.onSelectList}
              clearSelectionText={text.listPlaceholderText}
              isLoading={vm.isLoadingLists}
            />
          </Field>
        )}
      </SettingsSectionCard>

      {vm.selectedListId !== vm.noneOptionValue && (
        <SettingsSectionCard
          sectionOffset={sectionOffset + 1}
          title={t(
            "board.connections.catalogue.sourceSettings.sharepoint.sectionColumns",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.meFilterColumnLabelText}
            hint={text.meFilterColumnHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedMeFilterColumnId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedMeFilterColumnId
              }
              selectedLabel={vm.selectedMeFilterColumnName}
              query={meFilterColumnQuery}
              placeholder={text.meFilterColumnPlaceholderText}
              options={vm.peopleColumns.map((column) => ({
                id: column.id,
                label: column.displayName || column.name || column.id,
              }))}
              onQueryChange={setMeFilterColumnQuery}
              onSelect={vm.onSelectMeFilterColumn}
              clearSelectionText={text.meFilterColumnPlaceholderText}
              isLoading={vm.isLoadingColumns}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.statusFilterColumnLabelText}
            hint={text.statusFilterColumnHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedStatusFilterColumnId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedStatusFilterColumnId
              }
              selectedLabel={vm.selectedStatusFilterColumnName}
              query={statusFilterColumnQuery}
              placeholder={text.statusFilterColumnPlaceholderText}
              options={vm.choiceColumns.map((column) => ({
                id: column.id,
                label: column.displayName || column.name || column.id,
              }))}
              onQueryChange={setStatusFilterColumnQuery}
              onSelect={vm.onSelectStatusFilterColumn}
              clearSelectionText={text.statusFilterColumnPlaceholderText}
              isLoading={vm.isLoadingColumns}
            />
          </Field>

          {vm.selectedStatusFilterColumnId !== vm.noneOptionValue && (
            <>
              <Field
                orientation="horizontal"
                label={text.statusesToLoadLabelText}
                hint={text.statusesToLoadHintText}
              >
                <SourceMultiTagPicker
                  selectedIds={vm.statusesToLoad}
                  query={statusesToLoadQuery}
                  placeholder={text.statusesToLoadPlaceholderText}
                  options={vm.statusChoices.map((status) => ({
                    id: status,
                    label: status,
                  }))}
                  onQueryChange={setStatusesToLoadQuery}
                  onSelectionChange={vm.onSelectStatusesToLoad}
                  minQueryLength={0}
                  typeToSearchText={text.statusesToLoadHintText}
                  isLoading={vm.isLoadingColumns}
                />
              </Field>

              <Field
                orientation="horizontal"
                label={text.doneStatusLabelText}
                hint={text.doneStatusHintText}
                validationMessage={vm.statusReferenceErrorText || undefined}
              >
                <SourceSingleTagPicker
                  selectedId={
                    vm.doneStatus && vm.doneStatus !== vm.noneOptionValue
                      ? vm.doneStatus
                      : undefined
                  }
                  selectedLabel={
                    vm.doneStatus && vm.doneStatus !== vm.noneOptionValue
                      ? vm.doneStatus
                      : undefined
                  }
                  query={doneStatusQuery}
                  placeholder={text.doneStatusPlaceholderText}
                  options={vm.statusChoices.map((status) => ({
                    id: status,
                    label: status,
                  }))}
                  onQueryChange={setDoneStatusQuery}
                  onSelect={vm.onSelectDoneStatus}
                  clearSelectionText={text.doneStatusPlaceholderText}
                  isLoading={vm.isLoadingColumns}
                />
              </Field>
            </>
          )}

          <Field
            orientation="horizontal"
            label={text.dueDateColumnLabelText}
            hint={text.dueDateColumnHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedDueDateColumnId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedDueDateColumnId
              }
              selectedLabel={vm.selectedDueDateColumnName}
              query={dueDateColumnQuery}
              placeholder={text.dueDateColumnPlaceholderText}
              options={vm.dateColumns.map((column) => ({
                id: column.id,
                label: column.displayName || column.name || column.id,
              }))}
              onQueryChange={setDueDateColumnQuery}
              onSelect={vm.onSelectDueDateColumn}
              clearSelectionText={text.dueDateColumnPlaceholderText}
              isLoading={vm.isLoadingColumns}
            />
          </Field>
        </SettingsSectionCard>
      )}

      {(vm.isSearchingSites || vm.isLoadingLists || vm.isLoadingColumns) && (
        <Text>
          {vm.isSearchingSites
            ? text.loadingSitesText
            : vm.isLoadingLists
              ? text.loadingListsText
              : text.loadingColumnsText}
        </Text>
      )}

      {vm.siteSearchFailed && <Text>{text.siteSearchFailedText}</Text>}
      {vm.hasError && <Text>{text.loadErrorText}</Text>}

      {vm.statusesToLoad.length > 0 && (
        <StatusMappingSection
          sectionNumber={sectionOffset + 1}
          items={vm.statusesToLoad.map((s) => ({ id: s, name: s }))}
          mappings={
            ((editingConnection.configuration as Record<string, unknown>)
              ?.statusMappings as SourceStatusMapping[] | undefined) ?? []
          }
          boardStatuses={boardStatuses}
          onChange={(mappings) =>
            onUpdate({
              configuration: {
                ...(editingConnection.configuration as Record<string, unknown>),
                statusMappings: mappings,
              },
            })
          }
        />
      )}
    </>
  );
};
